/**
 * Structured shape of a zoom-call `extracted_fields` payload — the nested
 * requirements/pitches shape, distinct from the legacy flat key-value map
 * (`budget_min`, `location`, `bedrooms`, …).
 *
 * The backend serves `extractedFields` as an untyped `Record<string, any>`
 * passthrough; these interfaces describe the structured shape and are narrowed at
 * the render boundary via `isStructuredExtractedFields`. Field names mirror the
 * incoming JSON (snake_case); every field is optional/nullable — payloads are sparse.
 */

export interface MinMax {
  min?: string | number | null;
  max?: string | number | null;
  currency?: string | null;
}

/**
 * A numeric-ish field that may arrive either as a bare scalar (`budget: 15000000`)
 * or as a `{min,max}` range object. Formatters normalize both forms.
 */
export type Measure = MinMax | number | string;

/** Installment years may arrive as a scalar (`7`), a string, or a list (`["7"]`). */
export type Years = string[] | string | number;

export interface ValueSource {
  value: string;
  source?: string | null;
}

export interface FollowUp {
  promised?: boolean | null;
  when?: string | null;
}

export interface Objection {
  type?: string | null;
  quote?: string | null;
}

export interface PaymentPlan {
  down_payment?: Measure | null;
  monthly_installment?: Measure | null;
  installment_years?: Years | null;
  payment_structure?: string | null;
}

export interface Pitch {
  developer?: string | null;
  project?: string | null;
  unit_type?: string | null;
  bedrooms?: string[] | string | number | null;
  unit_area?: Measure | null;
  price?: Measure | null;
  price_alt_currency?: Measure | null;
  payment_plans?: PaymentPlan[] | null;
  delivery?: string[] | null;
  finishing?: string | null;
  amenities?: string[] | null;
  view?: string | null;
  rental_yield?: string | null;
  rental_income?: string | null;
  reservation_stage?: string | null;
  sale_type?: string | null;
  client_reaction?: string | null;
  client_reaction_evidence?: string | null;
  follow_up?: FollowUp | null;
  objection?: Objection | null;
}

export interface Requirement {
  label?: string | null;
  location?: ValueSource[] | null;
  unit_type?: ValueSource[] | null;
  bedrooms?: string[] | string | number | null;
  unit_area?: Measure | null;
  floor?: string | null;
  delivery?: string[] | null;
  finishing_type?: string | null;
  amenities?: string[] | null;
  sale_type?: string | null;
  client_objective?: string | null;
  timeline_to_buy?: string | null;
  buying_intent?: string | null;
  budget?: Measure | null;
  down_payment?: Measure | null;
  monthly_installment?: Measure | null;
  installment_years?: Years | null;
  pitches?: Pitch[] | null;
}

export interface MeetingScheduled {
  scheduled?: boolean | null;
  datetime?: string | null;
  medium?: string | null;
  location?: string | null;
}

export interface SiteVisitProposed {
  proposed?: boolean | null;
  date?: string | null;
}

export interface StructuredExtractedFields {
  requirements: Requirement[];
  budget_discussed?: boolean | null;
  offer_promised?: boolean | null;
  site_visit_proposed?: SiteVisitProposed | null;
  meeting_scheduled?: MeetingScheduled | null;
  follow_up_promised?: FollowUp | null;
  objections_raised?: string[] | null;
  buying_intent?: string | null;
  competitors_mentioned?: string[] | null;
  unresolved_questions?: string[] | null;
  seller_lead?: boolean | null;
  client_residency?: string | null;
  preferred_contact_channel?: string | null;
  best_time_to_contact?: string | null;
  client_notes?: string[] | null;
}

/**
 * Discriminates the structured extracted_fields shape (nested requirements/pitches)
 * from the legacy flat key-value map. The flat shape never carries a top-level
 * `requirements` array, so this is a zero-false-positive discriminator — and it
 * doubles as the TypeScript narrowing mechanism at the render boundary.
 */
export const isStructuredExtractedFields = (
  fields: Record<string, any> | null | undefined,
): fields is StructuredExtractedFields =>
  !!fields &&
  typeof fields === "object" &&
  Array.isArray((fields as Record<string, any>).requirements);
